/**
 * decoderService.js — Wave 4 client for the admin-api public decode
 * endpoint (routes/product_decoder_public.py).
 *
 * Offline-first contract:
 *   - ProductFinderPage runs the local Fuse.js search first (offline).
 *   - Only when the local grep misses does it call this service.
 *   - This service NEVER retries silently — a network fail bubbles up so
 *     the page can show a helpful inline error rather than a spinner.
 *
 * Env:
 *   VITE_ADMIN_API_PUBLIC_URL   base URL of the admin-api public endpoint
 *                                (no trailing slash). Defaults to
 *                                http://localhost:3001 for local dev.
 *                                Prod value is set at the wa-06 build step.
 */

const DEFAULT_BASE = 'http://localhost:3001';

function getBase() {
  const raw = import.meta.env.VITE_ADMIN_API_PUBLIC_URL;
  return (raw && raw.trim().replace(/\/$/, '')) || DEFAULT_BASE;
}

/**
 * Decode an SAP item code via the public admin-api endpoint.
 *
 * @param {string} itemCode
 * @param {Object} [opts]
 * @param {string} [opts.productLineHint]
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<object>} Decoded payload (see backend docstring).
 * @throws  {DecoderError} with .status when the server responds 4xx/5xx,
 *          or a plain Error when the fetch itself fails (offline etc.).
 */
export async function decodeItemCode(itemCode, opts = {}) {
  if (!itemCode || typeof itemCode !== 'string') {
    throw new DecoderError('item code is required', 400, 'EMPTY_ITEM_CODE');
  }
  const trimmed = itemCode.trim();
  if (!trimmed) {
    throw new DecoderError('item code is required', 400, 'EMPTY_ITEM_CODE');
  }
  const params = new URLSearchParams();
  if (opts.productLineHint) params.set('product_line_hint', opts.productLineHint);
  const qs = params.toString();
  const url = `${getBase()}/public/decode/${encodeURIComponent(trimmed)}${qs ? `?${qs}` : ''}`;

  let response;
  try {
    // NOTE: no request headers - keeps the fetch a "simple" CORS request
    // (per Fetch spec), so the browser skips the OPTIONS preflight that
    // the admin-api's app-wide CORSMiddleware would reject (it does not
    // know about MRT-scoped origins - that guard lives inside the route).
    response = await fetch(url, {
      method: 'GET',
      signal: opts.signal,
    });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') throw networkErr;
    throw new DecoderError(
      'Could not reach the decoder service — check your connection and retry.',
      0,
      'NETWORK',
    );
  }

  if (!response.ok) {
    let detail = null;
    try { detail = (await response.json())?.detail || null; } catch { /* text body */ }
    const msg = detail?.message
      || (response.status === 400 ? 'Item code shape is invalid.'
        : response.status === 403 ? 'This browser origin cannot reach the decoder.'
        : `Decoder returned HTTP ${response.status}.`);
    throw new DecoderError(msg, response.status, detail?.error || 'HTTP_ERROR');
  }

  return response.json();
}

/**
 * Custom error carrying an HTTP status + machine-readable code so the
 * page can branch on it (malformed vs offline vs 403).
 */
export class DecoderError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'DecoderError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Return true when the input looks like an SAP item code as opposed to
 * free-text — used by the finder page to decide whether to call the
 * decoder at all. Mirrors the server-side shape validator.
 */
export function looksLikeItemCode(input) {
  if (!input || typeof input !== 'string') return false;
  return /^[A-Za-z0-9][A-Za-z0-9-]{1,63}$/.test(input.trim());
}
